import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import Image1 from "../../images/exams/image1.png";
import SnackbarAlert from "../../components/SnackBar";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { buildExamFormData } from "../../helper/ExamFormData";
import { useConstants } from "../../hooks/UseConstants";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";

function CreateExam() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [course, setCourse] = useState(null);
    const [nameEn, setNameEn] = useState("");
    const [nameAr, setNameAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [durationMinutes, setDurationMinutes] = useState("30");
    const [passPercentage, setPassPercentage] = useState("50");
    const [maxAttempts, setMaxAttempts] = useState("3");
    const param = useParams();
    const navigate = useNavigate();

    const courseName = useMemo(() => {
        if (!course) return "";
        return isAr ? course.name_ar : course.name_en;
    }, [course, isAr]);

    const courseImage = resolveCourseImage(course);

    const loadCourse = async () => {
        setGetWait(true);
        const result = await Fetch(`${host}/courses/${param.id}/show`, "GET", null);
        if (result.status === 200) {
            setCourse(result.data.data);
        }
        setGetWait(false);
    };

    useEffect(() => {
        loadCourse();
    }, [param.id]);

    const createExam = async () => {
        if (!nameEn.trim() || !nameAr.trim()) {
            setSnackBar("error", isAr ? "أدخل اسم الاختبار بالعربية والإنجليزية" : "Enter exam name in Arabic and English");
            return;
        }
        if (!descriptionEn.trim() || !descriptionAr.trim()) {
            setSnackBar("error", isAr ? "أدخل وصف الاختبار بالعربية والإنجليزية" : "Enter exam description in Arabic and English");
            return;
        }

        setSendWait(true);

        const formData = buildExamFormData({
            nameEn: nameEn.trim(),
            nameAr: nameAr.trim(),
            descriptionEn: descriptionEn.trim(),
            descriptionAr: descriptionAr.trim(),
            courseId: param.id,
            durationMinutes,
            passPercentage,
            maxAttempts,
        });

        const result = await Fetch(`${host}/teacher/courses/exams/store`, "POST", formData);

        if (result.status === 200) {
            setSnackBar("success", isAr ? "تم إنشاء الاختبار وحفظه في الدورة" : "Exam created and saved to the course");
            navigate(`/course-details/${param.id}`);
        } else if (result.status === 422) {
            setSnackBar("error", result.data.errors?.[0] || result.data.message);
        } else {
            setSnackBar("error", isAr ? "فشل إنشاء الاختبار" : "Failed to create exam");
        }

        setSendWait(false);
    };

    const fieldSx = { width: "100%" };

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <PageHero
                    title={isAr ? "إنشاء اختبار" : "Create exam"}
                    subtitle={isAr ? "أضف اختباراً جديداً واربطه بالدورة" : "Add a new exam linked to your course"}
                    icon={<AssignmentIcon />}
                    actions={
                        <>
                            <Button
                                variant="contained"
                                startIcon={sendWait ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                onClick={createExam}
                                disabled={sendWait}
                                sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                            >
                                {isAr ? "إضافة اختبار" : "Add exam"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(`/course-details/${param.id}`)}
                                sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                            >
                                {isAr ? "عودة للدورة" : "Back to course"}
                            </Button>
                        </>
                    }
                />

                {course ? (
                    <ScrollReveal>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: theme.palette.background.paper,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    bgcolor: "#0D358C",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {courseImage ? (
                                    <Box component="img" src={courseImage} alt={courseName} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                    <MenuBookOutlinedIcon sx={{ color: "white", fontSize: 36 }} />
                                )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isAr ? "الدورة المختارة" : "Selected course"}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }} noWrap>
                                    {courseName}
                                </Typography>
                                <Chip size="small" label={`ID #${course.id}`} sx={{ mt: 0.5 }} />
                            </Box>
                        </Box>
                    </ScrollReveal>
                ) : null}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        direction: "ltr",
                        gap: 3,
                        alignItems: "stretch",
                    }}
                >
                    {/* Illustration — always left */}
                    <ScrollReveal delay={50} className="w-full lg:w-[38%]">
                        <Box
                            sx={{
                                height: "100%",
                                minHeight: { lg: 420 },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                p: 4,
                                borderRadius: 4,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                background: theme.palette.mode === "dark"
                                    ? "linear-gradient(160deg, #1a2744 0%, #121212 100%)"
                                    : "linear-gradient(160deg, #e8f0fa 0%, #f5f8fc 100%)",
                            }}
                        >
                            <Box
                                component="img"
                                src={Image1}
                                alt=""
                                sx={{
                                    width: "100%",
                                    maxWidth: 300,
                                    height: "auto",
                                    objectFit: "contain",
                                }}
                            />
                            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", maxWidth: 280, lineHeight: 1.7 }}>
                                {isAr
                                    ? "بعد إنشاء الاختبار يمكنك إضافة الأسئلة وخيارات الإجابة من صفحة تفاصيل الدورة"
                                    : "After creating the exam, add questions and answer options from the course details page"}
                            </Typography>
                        </Box>
                    </ScrollReveal>

                    {/* Form — always right */}
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0, direction: isAr ? "rtl" : "ltr" }}>
                        <ScrollReveal delay={100}>
                            <DashboardPanelCard
                                title={isAr ? "معلومات الاختبار" : "Exam information"}
                                subtitle={isAr ? "يُحفظ في قاعدة البيانات ويظهر للطلاب" : "Saved to the database and shown to students"}
                            >
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                                        <TextField
                                            sx={fieldSx}
                                            value={nameEn}
                                            onChange={(e) => setNameEn(e.target.value)}
                                            label={isAr ? "اسم الاختبار بالإنجليزية" : "Exam name (English)"}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                        <TextField
                                            sx={fieldSx}
                                            value={nameAr}
                                            onChange={(e) => setNameAr(e.target.value)}
                                            label={isAr ? "اسم الاختبار بالعربية" : "Exam name (Arabic)"}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    </Box>
                                    <TextField
                                        sx={fieldSx}
                                        value={descriptionEn}
                                        onChange={(e) => setDescriptionEn(e.target.value)}
                                        label={isAr ? "وصف الاختبار بالإنجليزية" : "Exam description (English)"}
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        minRows={3}
                                        required
                                    />
                                    <TextField
                                        sx={fieldSx}
                                        value={descriptionAr}
                                        onChange={(e) => setDescriptionAr(e.target.value)}
                                        label={isAr ? "وصف الاختبار بالعربية" : "Exam description (Arabic)"}
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        minRows={3}
                                        required
                                    />
                                </Box>
                            </DashboardPanelCard>
                        </ScrollReveal>

                        <ScrollReveal delay={150}>
                            <DashboardPanelCard
                                title={isAr ? "إعدادات الاختبار" : "Exam settings"}
                                subtitle={isAr ? "مدة الاختبار ونسبة النجاح وعدد المحاولات" : "Duration, pass score, and attempt limits"}
                            >
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                                    <TextField
                                        sx={fieldSx}
                                        type="number"
                                        value={durationMinutes}
                                        onChange={(e) => setDurationMinutes(e.target.value)}
                                        label={isAr ? "المدة (دقيقة)" : "Duration (minutes)"}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TimerOutlinedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ min: 1, max: 600 }}
                                    />
                                    <TextField
                                        sx={fieldSx}
                                        type="number"
                                        value={passPercentage}
                                        onChange={(e) => setPassPercentage(e.target.value)}
                                        label={isAr ? "نسبة النجاح %" : "Pass %"}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PercentOutlinedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ min: 0, max: 100 }}
                                    />
                                    <TextField
                                        sx={fieldSx}
                                        type="number"
                                        value={maxAttempts}
                                        onChange={(e) => setMaxAttempts(e.target.value)}
                                        label={isAr ? "عدد المحاولات (0 = غير محدود)" : "Max attempts (0 = unlimited)"}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <ReplayOutlinedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ min: 0 }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1.5,
                                        justifyContent: "flex-end",
                                        mt: 3,
                                        pt: 2,
                                        borderTop: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate(`/course-details/${param.id}`)}
                                        disabled={sendWait}
                                    >
                                        {isAr ? "إلغاء" : "Cancel"}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={createExam}
                                        disabled={sendWait}
                                        startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                                        sx={{
                                            minWidth: 200,
                                            px: 3,
                                            bgcolor: "#0D358C",
                                            fontWeight: 700,
                                            boxShadow: "0 4px 14px rgba(13,53,140,0.35)",
                                            "&:hover": { bgcolor: "#1d4ed8" },
                                        }}
                                    >
                                        {sendWait ? (isAr ? "جاري الحفظ..." : "Saving...") : (isAr ? "إضافة اختبار" : "Add exam")}
                                    </Button>
                                </Box>
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Box>
                </Box>
            </PageContent>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default CreateExam;
